import React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary that prevents unhandled render errors
 * from crashing the entire React tree to a blank screen.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] Uncaught render error:", error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-surface-900 flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <div className="bg-surface-800 rounded-2xl border border-border p-8 shadow-xl">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-health-500/15 flex items-center justify-center">
                <svg className="w-6 h-6 text-health-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-h3 font-bold text-text-primary mb-2">
                Something went wrong
              </h2>
              <p className="text-body-sm text-text-secondary mb-6">
                An unexpected error occurred. This has been logged automatically.
              </p>
              {process.env.NODE_ENV === "development" && this.state.error && (
                <pre className="mb-6 p-3 rounded-lg bg-surface-900 border border-border text-left text-caption text-health-400 overflow-x-auto max-h-32">
                  {this.state.error.message}
                </pre>
              )}
              <div className="flex gap-3 justify-center">
                <button onClick={this.handleGoHome} className="btn-secondary">
                  Go Home
                </button>
                <button onClick={this.handleReload} className="btn-primary">
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Inline error fallback for use inside layouts (keeps sidebar/nav visible).
 */
export function RouteErrorFallback({ error, onRetry }: { error?: Error | null; onRetry?: () => void }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] px-6">
      <div className="w-full max-w-md text-center">
        <div className="bg-surface-800 rounded-2xl border border-border p-8 shadow-xl">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-health-500/15 flex items-center justify-center">
            <svg className="w-6 h-6 text-health-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-h3 font-bold text-text-primary mb-2">
            Page failed to load
          </h2>
          <p className="text-body-sm text-text-secondary mb-6">
            Something went wrong loading this page. Try reloading.
          </p>
          {error && (
            <pre className="mb-6 p-3 rounded-lg bg-surface-900 border border-border text-left text-caption text-health-400 overflow-x-auto max-h-32">
              {error.message}
            </pre>
          )}
          <div className="flex gap-3 justify-center">
            <button onClick={onRetry ?? (() => window.location.reload())} className="btn-primary">
              Reload Page
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Route-level error boundary that shows error inline (preserving layout chrome).
 */
export class RouteErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[RouteErrorBoundary] Uncaught render error:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return <RouteErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}
